import {useNavigate, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {type Column} from '@/components/data-table'
import {BusinessDirectoryCard} from '@/components/business-directory-card'
import {CreateBusinessDialog} from '@/components/create-business-dialog'
import {ListEmptyState} from '@/components/section-card'
import {PageHeading} from '@/components/page-heading'
import {
  useBusinesses,
  useIsSystemUser,
  type Business,
  type BusinessFiltro,
} from '@/hooks/empresa/organizacion/use-businesses'
import {useTableState} from '@/hooks/common/use-table-state'
import {businessNameNitColumns} from '@/lib/business-columns'
import {SYSTEM_NIT} from '@/lib/constants'
import {SearchInput} from '@/components/ui/search-input'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

const VALID_FILTROS: BusinessFiltro[] = ['evaluacion_en_curso', 'requiere_atencion']

/** `?filtro=` usa el mismo filtro server-side que core/admin_attention.py, nunca uno propio. */
export function BusinessesPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const isSystemUser = useIsSystemUser()
  const table = useTableState('name')
  const [searchParams, setSearchParams] = useSearchParams()
  const filtroParam = searchParams.get('filtro')
  const filtro = VALID_FILTROS.find((f) => f === filtroParam)
  const {data, isLoading} = useBusinesses({
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
    filtro,
  })

  function clearFiltro() {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.delete('filtro')
        return params
      },
      {replace: true}
    )
  }

  const columns: Column<Business>[] = [
    ...businessNameNitColumns(t),
    ...(isSystemUser
      ? [
          {
            key: 'created_at',
            header: t('businesses.table.createdAt'),
            sortable: true,
            width: 'w-24 sm:w-32',
            cell: (b: Business) => (
              <span className="block truncate font-mono text-xs text-muted-foreground">
                {dateFormat.format(new Date(b.created_at))}
              </span>
            ),
          },
        ]
      : [
          {
            key: 'role',
            header: t('businesses.table.role'),
            width: 'w-32',
            cell: (b: Business) => <Tag tone="neutral">{t(`roles.${b.role}`)}</Tag>,
          },
        ]),
  ]

  return (
    <div className="space-y-5">
      <PageHeading
        title={t('businesses.title')}
        accion={isSystemUser && <CreateBusinessDialog trigger={<Button size="sm">{t('businesses.new')}</Button>} />}
      />

      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <BusinessDirectoryCard
          table={table}
          data={data}
          columns={columns}
          toolbar={
            <>
              <SearchInput
                value={table.searchInput}
                onChange={table.setSearchInput}
                placeholder={t('businesses.searchPlaceholder')}
              />
              {filtro && (
                <Tag tone="neutral" data-testid="businesses-filtro-active">
                  {t(`businesses.filtro.${filtro}`)}
                  <button
                    type="button"
                    data-testid="businesses-filtro-clear"
                    aria-label={t('businesses.filtro.clear')}
                    onClick={clearFiltro}
                    className="ml-1.5 hover:text-foreground">
                    ×
                  </button>
                </Tag>
              )}
            </>
          }
          onRowClick={(b) => {
            // El admin nunca opera la empresa en sitio: va a su panel; el modo dios es la única puerta.
            if (!isSystemUser) navigate(`/businesses/${b.id}`)
            else if (b.nit !== SYSTEM_NIT) navigate(`/admin/empresas/${b.id}`)
          }}
          emptyState={
            <ListEmptyState
              page
              title={
                table.search
                  ? t('businesses.noResults')
                  : filtro
                    ? t(`businesses.filtro.${filtro}Empty`)
                    : t('businesses.emptyTitle')
              }
              body={!table.search && !filtro ? t('businesses.emptyBody') : undefined}
            />
          }
        />
      )}
    </div>
  )
}
